/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'../common/NonVIPREventTarget',
	'underscore',
	'../VisEventHandler',
	'../../../lib/@waca/dashboard-common/dist/utils/EventChainLocal',
	'../../../util/EventUtils'
], function(
	$,
	NonVIPREventTarget,
	_,
	VisEventHandler,
	EventChainLocal,
	EventUtils
) {

	class GridEventTarget extends NonVIPREventTarget {
		constructor (...args) {
			super(...args);
			const options = args[0] || {};
			this.visControl = options.visControl;
			// map from visDecorations to viprDecoration
			this.viprDecorations = {};
			this.viprDecorations[VisEventHandler.DECORATIONS.HIGHLIGHT] = 'highlighted';
			this.viprDecorations[VisEventHandler.DECORATIONS.SELECT] = 'selected';
			return this.getAPI();
		}

		/**
		 * Get the array of event targets based on the event.
		 * Event target represents a data point object that corresponds to the event.
		 *
		 * @param event	Event object
		 */
		getEventTargets (event) {
			const node = this._getTargetNode(event);
			if(!node) {
				return [];
			}

			var $node = $(node);
			var row = $node.attr('row');
			if(_.isUndefined(row) || _.isUndefined(this.visControl._dataRows[row]) ||
				this._isSummaryRow($node) || (!this._canProcessKeyDown(event) && !this.mouseWithinBoundCheck(event))) {
				return [];
			}

			if (this._isHeaderRow($node)) {
				return this._getHeaderTargets(event, $node);
			} else {
				return this._getRowTargets(event, $node);
			}
		}

		remove () {
			this.visControl = null;
			super.remove();
		}

		_isSummaryRow (eventTarget) {
			return $(eventTarget).hasClass('summary');
		}

		_isHeaderRow (eventTarget) {
			return $(eventTarget).hasClass('header-cell');
		}

		_getHeaderSelectionContext (selectedHeaders) {
			let selectionContext = {
				mapIndex: [],
				slotId: 'grid_cols',
				actions: { actionsToKeep: [], actionOptions: {} }
			};

			_.each(selectedHeaders, (header) => {
				const col = $(header).attr('col');
				// Find position of data item in the slot
				const { mapIndex } = this.visControl.findSlotDetails(col);
				selectionContext.mapIndex.push(mapIndex);

				if (this.visControl.isRank(col)) {
					selectionContext.actions.actionsToKeep.push('sortAction');
					selectionContext.actions.actionOptions.sortAction = { rankSort: true };
				}
			});

			return selectionContext;
		}

		_getHeaderTargets (event, $node) {
			let targets = [];
			const row = parseInt($node.attr('row'));

			if (['click', 'contextmenu', 'hold', 'keydown'].includes(event.type)){
				const target = this._getTargetNode(event);
				this.visControl.handleSelectColumnHeader(target, !(event.ctrlKey || event.metaKey), event.type === 'contextmenu');
			}
			const selectedHeaders = this.visControl.getSelectedColumnHeaderNodes();

			if (selectedHeaders && selectedHeaders.length > 0) {
				targets.push({
					key: row + ':',
					type: 'itemclass',
					source: $node,
					isEdgeSelect: true,
					values: this._getColumnTargetValues(selectedHeaders, row),
					selectionContext: this._getHeaderSelectionContext(selectedHeaders),
					area: 'visualization'
				});
			} else {
				// User deselects only selected header, resulting in nothing selected.
				// No items are selected (expected), but also don't want to enable
				// widget ODT from appearing later on.
				const eventChainLocal = new EventChainLocal(event);
				eventChainLocal.setProperty('preventDefaultContextBar', true);
			}

			return targets;
		}

		_getColumnTargetValues (selectedHeaders, row) {
			let slots = [];
			_.each(selectedHeaders, (header) => {
				const col = $(header).attr('col');
				const { slotIndex } = this.visControl.findSlotDetails(col);
				let value = this.visControl._dataRows[row][col];
				value = _.isUndefined(value.value) ? value : value.value;
				value = _.isArray(value) ? value[0] : value;	//The grid doesn't stack values, so only the first value in the array is used
				slots[slotIndex] = { value: value };
			});

			return slots;
		}

		_getRowTargets (event, $node) {
			let targets = [];
			const row = parseInt($node.attr('row'));
			const col = parseInt($node.attr('col'));
			const isGrouped = this.visControl.isGroupedColumn(col);
			const numberOfColumns = isGrouped ? col+1 : this.visControl._dataRows[row].length;
			//We treat the grid values row as item when its a left click to not filter with ordinal values.
			const treatItAsItem = (event.type === 'click' && event.which === 1) || (event.type === 'tap');

			targets.push({
				key: row + ':' + (numberOfColumns-1),
				type: treatItAsItem ? 'item' : 'datapoint',
				source: $node,
				isEdgeSelect: true,
				values: this._getRowTargetValues(row, numberOfColumns),
				area: 'visualization'
			});

			return targets;
		}

		_getRowTargetValues (row, numberOfColumns) {

			let slots = [];
			let values = [];
			let value;

			for(let column = 0; column < numberOfColumns; column++) {
				//the reason that we treat it as a datapoint is because, when we select on the cell, its behavior is datapoint, hover and right-click should follow the same type to avoid confusion
				if(!this.visControl.isRank(column)){
					value = this.visControl._dataRows[row][column];
					value = _.isUndefined(value.value) ? value : value.value;
					value = _.isArray(value) ? value[0] : value;	//The grid doesn't stack values, so only the first value in the array is used
					values.push(EventUtils.toDeprecatedPayload(value));
				}
			}
			slots.push(values); //In the grid, value is always in the first slot

			return slots;
		}
	}
	return GridEventTarget;
});
